import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import * as ApplicationProductsService from '../services/ApplicationProductsService';
import { ApplicationProduct } from '../types';
import { notifyError, notifySuccess } from '../../../components/PlatformNotification';

// Query keys
export const applicationProductsKeys = {
    all: ['applicationProducts'] as const,
    lists: () => [...applicationProductsKeys.all, 'list'] as const,
    list: (filters: Record<string, string>) => [...applicationProductsKeys.lists(), { filters }] as const,
    details: () => [...applicationProductsKeys.all, 'detail'] as const,
    detail: (id: string) => [...applicationProductsKeys.details(), id] as const,
};

// Hooks
export const useApplicationProductsQuery = (filters: Record<string, string> = {}) => {
    return useQuery({
        queryKey: applicationProductsKeys.list(filters),
        queryFn: () => ApplicationProductsService.fetchRecords(filters),
    });
};

export const useApplicationProductQuery = (id: string) => {
    return useQuery({
        queryKey: applicationProductsKeys.detail(id),
        queryFn: () => ApplicationProductsService.fetch(id),
        enabled: !!id,
    });
};

export const useCreateApplicationProductMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ApplicationProductsService.add,
        onSuccess: () => {
            notifySuccess('Application product created');
            queryClient.invalidateQueries({ queryKey: applicationProductsKeys.lists() });
        },
        onError: notifyError,
    });
};

export const useUpdateApplicationProductMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, record }: { id: string; record: ApplicationProduct }) =>
            ApplicationProductsService.update(id, record),
        onSuccess: (_, { id }) => {
            notifySuccess('Application product updated');
            queryClient.invalidateQueries({ queryKey: applicationProductsKeys.lists() });
            queryClient.invalidateQueries({ queryKey: applicationProductsKeys.detail(id) });
        },
        onError: notifyError,
    });
};

export const useDeleteApplicationProductMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ApplicationProductsService.deleteRecord,
        onSuccess: () => {
            notifySuccess('Application product deleted');
            queryClient.invalidateQueries({ queryKey: applicationProductsKeys.all });
        },
        onError: notifyError,
    });
};

// Global filter state (shared across components)
let globalFilters: Record<string, string> = {};
let filterListeners: Set<(filters: Record<string, string>) => void> = new Set();

const notifyFilterChange = (filters: Record<string, string>) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

export const useApplicationProductsFilter = () => {
    const [filters, setFilters] = useState<Record<string, string>>(globalFilters);

    useEffect(() => {
        const listener = (newFilters: Record<string, string>) => {
            setFilters(newFilters);
        };
        filterListeners.add(listener);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    const handleSetFilters = (newFilters: Record<string, string>) => {
        notifyFilterChange(newFilters);
    };

    const handleClearFilters = () => {
        notifyFilterChange({});
    };

    return {
        filters,
        handleSetFilters,
        handleClearFilters,
    };
};
